import React from 'react';

/**
 * Yapp Button — the signature gradient primary, plus secondary, ghost,
 * danger and guest variants. Lifts 2px on hover.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  icon = null,
  iconRight = null,
  children,
  style = {},
  ...rest
}) {
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);

  const sizes = {
    sm: { padding: '0.4rem 0.85rem', font: 'var(--text-sm)' },
    md: { padding: '0.6rem 1.25rem', font: 'var(--text-base)' },
    lg: { padding: '0.85rem 1.75rem', font: 'var(--text-md)' },
  };

  const palette = {
    primary: {
      background: 'var(--brand-gradient)',
      color: 'var(--text-on-brand)',
      border: '1px solid transparent',
      shadow: hover ? 'var(--glow-brand)' : 'none',
    },
    secondary: {
      background: hover ? 'var(--brand-100)' : 'var(--brand-50)',
      color: 'var(--brand-600)',
      border: '1px solid var(--brand-100)',
      shadow: 'none',
    },
    ghost: {
      background: hover ? 'var(--surface-sunken)' : 'transparent',
      color: 'var(--text-body)',
      border: '1px solid transparent',
      shadow: 'none',
    },
    danger: {
      background: hover ? 'var(--danger-500)' : 'var(--danger-400)',
      color: '#fff',
      border: '1px solid transparent',
      shadow: 'none',
    },
    guest: {
      background: hover ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.08)',
      color: 'var(--text-on-dark)',
      border: '1px solid var(--border-on-dark)',
      shadow: 'none',
    },
  };

  const p = palette[variant] || palette.primary;
  const s = sizes[size] || sizes.md;

  return (
    <button
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        width: fullWidth ? '100%' : 'auto',
        padding: s.padding,
        fontSize: s.font,
        fontFamily: 'var(--font-sans)',
        fontWeight: 'var(--weight-semibold)',
        lineHeight: 1,
        background: p.background,
        color: p.color,
        border: p.border,
        borderRadius: 'var(--radius-md)',
        boxShadow: p.shadow,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transform: disabled ? 'none' : (active ? 'translateY(0)' : (hover ? 'var(--lift-hover)' : 'translateY(0)')),
        transition: 'transform var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard)',
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}
